import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, CheckCheck, Send, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create notification sound using Web Audio API
const createNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant notification sound (two-tone chime)
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(880, now, 0.15);  // A5
    playTone(1100, now + 0.15, 0.2);  // C#6
    
    return true;
  } catch (error) {
    console.error('Audio playback failed:', error);
    return false;
  }
};

// Vibrate device if supported
const vibrateDevice = (pattern = [100, 50, 100]) => {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
      return true;
    }
  } catch (error) {
    console.error('Vibration failed:', error);
  }
  return false;
};

const NotificationBell = ({ user, onEventChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [newNotification, setNewNotification] = useState({ title: '', message: '', target_roles: [] });
  const [sending, setSending] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSound');
    return saved !== 'false'; // Default to true
  });
  const dropdownRef = useRef(null);
  const lastNotificationId = useRef(null);

  // Save sound preference
  useEffect(() => {
    localStorage.setItem('notificationSound', soundEnabled.toString());
  }, [soundEnabled]);

  // Alert user with sound and vibration
  const alertUser = useCallback(() => {
    if (soundEnabled) {
      createNotificationSound();
    }
    vibrateDevice([100, 50, 100, 50, 100]); // Double vibration pattern
  }, [soundEnabled]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Poll for new notifications
  useEffect(() => {
    fetchNotifications();
    const pollInterval = setInterval(() => {
      checkUnreadCount();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, getAuthHeaders());
      const newNotifications = response.data.notifications;
      setNotifications(newNotifications);
      setUnreadCount(response.data.unread_count);

      // Check if there's a new notification and show toast
      if (newNotifications.length > 0 && lastNotificationId.current) {
        const latestId = newNotifications[0]?.id;
        if (latestId !== lastNotificationId.current) {
          const latestNotification = newNotifications[0];
          if (!latestNotification.is_read) {
            // Play sound and vibrate
            alertUser();
            
            toast.info(latestNotification.title, {
              description: latestNotification.message,
              duration: 5000
            });
            
            // If it's an event change notification, trigger refresh
            if (latestNotification.type === 'event_change' && onEventChange) {
              onEventChange();
            }
          }
        }
      }
      
      if (newNotifications.length > 0) {
        lastNotificationId.current = newNotifications[0]?.id;
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const checkUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, getAuthHeaders());
      const newCount = response.data.unread_count;
      
      // If count increased, fetch full notifications to show toast
      if (newCount > unreadCount) {
        fetchNotifications();
      } else {
        setUnreadCount(newCount);
      }
    } catch (error) {
      console.error('Failed to check unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, getAuthHeaders());
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const sendNotification = async () => {
    if (!newNotification.title.trim() || !newNotification.message.trim()) {
      toast.error('Please enter title and message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/notifications`, newNotification, getAuthHeaders());
      const targetMsg = newNotification.target_roles.length === 0 
        ? 'all users' 
        : `${newNotification.target_roles.join(' and ')}s`;
      toast.success(`Notification sent to ${targetMsg}`);
      setShowSendDialog(false);
      setNewNotification({ title: '', message: '', target_roles: [] });
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const toggleRole = (role) => {
    setNewNotification(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative text-zinc-400 hover:text-white"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown - Fullscreen on mobile */}
      {showDropdown && (
        <div className="fixed inset-0 sm:absolute sm:inset-auto sm:right-0 sm:mt-2 sm:w-80 bg-[#18181b] sm:border border-[#27272a] sm:rounded-lg shadow-xl z-50 flex flex-col sm:max-h-96">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-3 border-b border-[#27272a]">
            <span className="font-bold text-white text-lg sm:text-base">Notifications</span>
            <div className="flex gap-1 items-center">
              {/* Sound Toggle */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`h-8 sm:h-7 px-2 ${soundEnabled ? 'text-[#facc15] hover:text-[#eab308]' : 'text-zinc-500 hover:text-zinc-300'}`}
                title={soundEnabled ? 'Sound On' : 'Sound Off'}
                data-testid="sound-toggle-btn"
              >
                {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </Button>
              {(user.active_role === 'admin' || user.active_role === 'scorer' || user.role === 'admin' || user.role === 'scorer') && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowSendDialog(true);
                    setShowDropdown(false);
                  }}
                  className="text-[#facc15] hover:text-[#eab308] h-8 sm:h-7 px-2"
                  data-testid="send-notification-btn"
                >
                  <Send size={14} className="mr-1" />
                  Send
                </Button>
              )}
              {unreadCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={markAllAsRead}
                  className="text-zinc-400 hover:text-white h-8 sm:h-7 px-2"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck size={16} />
                </Button>
              )}
              {/* Close button - visible on mobile */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDropdown(false)}
                className="text-zinc-400 hover:text-white h-8 sm:hidden px-2"
                data-testid="close-notifications-btn"
              >
                <X size={20} />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto sm:max-h-72">
            {notifications.length === 0 ? (
              <div className="p-8 sm:p-4 text-center text-zinc-500">
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 sm:p-3 border-b border-[#27272a] hover:bg-[#27272a] cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-[#1e1e21]' : ''
                  }`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-3 sm:gap-2">
                    <div className={`mt-1.5 sm:mt-1 w-2.5 sm:w-2 h-2.5 sm:h-2 rounded-full flex-shrink-0 ${
                      !notification.is_read ? 'bg-[#facc15]' : 'bg-transparent'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-white text-base sm:text-sm truncate">
                          {notification.title}
                        </span>
                        <span className="text-xs text-zinc-500 flex-shrink-0">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-zinc-400 text-sm mt-1 line-clamp-3 sm:line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2 sm:mt-1">
                        <span className="text-xs text-zinc-500">
                          From: {notification.sender_name}
                        </span>
                        {notification.type === 'event_change' && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">
                            Event
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Send Notification Dialog (Admin Only) */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#facc15]">
              Send Notification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Title</Label>
              <Input
                value={newNotification.title}
                onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                placeholder="Notification title"
                data-testid="notification-title-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Message</Label>
              <textarea
                value={newNotification.message}
                onChange={(e) => setNewNotification(prev => ({ ...prev, message: e.target.value }))}
                className="mt-1 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md p-2 min-h-24 resize-none"
                placeholder="Enter your message..."
                data-testid="notification-message-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-2 block">Target Users</Label>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setNewNotification(prev => ({ ...prev, target_roles: [] }))}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    newNotification.target_roles.length === 0
                      ? 'bg-[#facc15] text-black'
                      : 'bg-[#27272a] text-zinc-300 hover:bg-[#3f3f46]'
                  }`}
                  data-testid="target-all"
                >
                  All Users
                </button>
                {['marshal', 'scorer'].map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRole(role)}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors capitalize ${
                      newNotification.target_roles.includes(role)
                        ? 'bg-[#facc15] text-black'
                        : 'bg-[#27272a] text-zinc-300 hover:bg-[#3f3f46]'
                    }`}
                    data-testid={`target-${role}`}
                  >
                    {role}s Only
                  </button>
                ))}
              </div>
            </div>
            <Button
              onClick={sendNotification}
              disabled={sending}
              className="w-full bg-[#facc15] text-black hover:bg-[#eab308] h-12 font-bold"
              data-testid="send-notification-submit"
            >
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationBell;
